import useQueryParams from '@common/src/hooks/useQueryParams';
import { FLOWS_CONFIG_PATH } from '@flows/src/FlowsConfigRouter';
import { useYellowhiveFlowsConfigureStore } from '@flows/src/store/zustand/flows-configure/yellowhiveFlowsConfigureStore';
import { useYellowhiveFlowsProducts } from '@flows/src/hooks/flows-configure/useYellowhiveFlowsProducts';
import { PartyType } from '@parties/src/types';
import QuestionViewSkeleton from '@sales-conversation/src/components/skeletons/QuestionViewSkeleton';
import { FC, useEffect } from 'react';
import { useHistory, useParams } from 'react-router';
import {
  GetYellowhiveflowProductsResponse,
  SchemaYellowHiveProductsInConversationCmd,
} from 'wg-api-sdk';
import useStartYellowhiveFlow from '@flows/src/hooks/flows-configure/useStartYellowhiveFlow';

const getProductsFromConfig = (
  catalogue: GetYellowhiveflowProductsResponse['items'],
  selectedInsurances: { code: string; name: string }[],
  selectedCoverages: Record<string, string[]>,
): SchemaYellowHiveProductsInConversationCmd[] => {
  if (!catalogue || !selectedInsurances.length) {
    return [];
  }

  const productsInConversation: SchemaYellowHiveProductsInConversationCmd[] =
    [];

  for (const catalogueProduct of catalogue) {
    // if not selected insurance in preflow, skip
    // if (
    //   !selectedInsurances.some(
    //     (insurance) => insurance.code === catalogueProduct.insurance.code,
    //   )
    // ) {
    //   continue;
    // }

    // Get selected coverages for this insurance
    const selectedCoveragesForInsurance =
      selectedCoverages[catalogueProduct.insurance.code] || [];

    // Filter coverages to only include selected ones that exist in the catalogue product
    const filteredCoverages = catalogueProduct.coverages
      .filter((coverage) =>
        selectedCoveragesForInsurance.includes(coverage.code),
      )
      .map((coverage) => coverage.code);

    // Only include products that have at least one selected coverage
    if (filteredCoverages.length > 0) {
      const product: SchemaYellowHiveProductsInConversationCmd = {
        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
        // @ts-ignore SDK type may lag behind backend shape
        id: catalogueProduct.id,
        coverages: filteredCoverages,
      };
      productsInConversation.push(product);
    }
  }

  return productsInConversation;
};

const FlowsConfigureYellowhiveFinish: FC = () => {
  const queryParams = useQueryParams();
  const partyType = (queryParams.get('type') as PartyType) || 'CUSTOMER';
  const { flowId } = useParams<{ flowId: string }>();
  const history = useHistory();
  const startYellowhiveFlow = useStartYellowhiveFlow();

  // Get selected insurances and coverages from yellowhive store
  const insurances = useYellowhiveFlowsConfigureStore(
    (state) => state.insurances,
  );
  const coverages = useYellowhiveFlowsConfigureStore(
    (state) => state.coverages,
  );

  // Prepare request for products API
  const productsRequest =
    insurances.length > 0
      ? {
          insurance_codes: insurances.map((insurance) => insurance.code),
          coverage_codes: Object.values(coverages).flat(),
          partyType,
        }
      : null;

  // Fetch products using the API
  const { data: productsData, isSuccess } = useYellowhiveFlowsProducts(
    productsRequest || { insurance_codes: [], coverage_codes: [], partyType },
  );

  useEffect(() => {
    // If the required data is not available, exit early
    if (!productsData || !flowId) return;

    // If no insurances are selected, redirect the user back to the configuration page
    if (!insurances.length) {
      return history.push({
        pathname: `${FLOWS_CONFIG_PATH}/${flowId}`,
        search: window.location.search,
      });
    }

    // Extract the list of products from the catalogue
    const { items } = productsData;

    // Generate the list of products based on the selected insurances and coverages
    const products = getProductsFromConfig(items, insurances, coverages);
    // Start the yellowhive flow with the generated products
    // Preserve the type query parameter in the inputUrl for back navigation
    const currentType = queryParams.get('type');
    const inputUrl = currentType
      ? `${FLOWS_CONFIG_PATH}/${flowId}?type=${currentType}`
      : `${FLOWS_CONFIG_PATH}/${flowId}`;

    startYellowhiveFlow({
      flowId: flowId,
      inputUrl,
      partyId: queryParams.get('partyId') || undefined,
      yellowhive_products: products,
    });
  }, [isSuccess, productsData, flowId, insurances, coverages, history]);

  return <QuestionViewSkeleton />;
};

export default FlowsConfigureYellowhiveFinish;
